import React, { useEffect, useState } from "react";
import { Modal, FormGroup, ControlLabel, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../Utils/Loaders";
import { attachmentEntityType, conceptType } from "../../../variables/Enums";
import { SnomedPicker } from "../../CustomControls/SnomedPicker";
import { CustomDate } from "../../CustomControls/CustomDateTime";
import { getDevice } from "../../../api/devices";
import { HandleApiError } from "../../Notifications/APIErrorHandler";
import DocumentUploadCard from "../../CustomControls/DocumentUpload/DocumentUploadCard";
import { AuditTrail } from "../../../containers/Admin/AuditTrail";
import { AuditModal } from "../../AuditTrail/AuditModal";

export const ModalEditDevice = ({ deviceId, show, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    device: null,
    name: "",
    serialNumber: "",
    attachmentFile: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      getDevice(deviceId)
        .then((response) => {
          setFormData(response.data);
          setLoading(false);
        })
        .catch((error) => {
          HandleApiError(error);
          setLoading(false);
        });
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    if (!e?.target) {
      return;
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const payload = {
      name: formData.name,
      serialNumber: formData.serialNumber,
      calibrationDueDate: formData.attachmentFile?.dueDate,
    };

    setSaving(true);

    onSave(payload, setSaving);
  };

  const handleAuditTrail = () => {
    setShowAuditModal(true);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        dialogClassName="rounded-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("devices.editModal.title")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {(loading || saving) && <Spinner />}
          {!loading && !saving && (
            <>
              <FormGroup controlId="formName">
                <ControlLabel>{t("devices.editModal.nameLabel")}</ControlLabel>
                <input
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup controlId="serialNumber">
                <ControlLabel>
                  {t("devices.editModal.serialNumberLabel")}
                </ControlLabel>
                <input
                  type="text"
                  id="serialNumber"
                  name="serialNumber"
                  className="form-control"
                  value={formData.serialNumber || ""}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup controlId="formDevice">
                <ControlLabel>
                  {t("devices.editModal.deviceLabel")}
                </ControlLabel>
                <SnomedPicker
                  conceptType={conceptType.events}
                  defaultSelected={formData.device ? [formData.device] : []}
                  id="device"
                  value={formData.device}
                  name="device"
                  disabled
                  placeholder={t("commons.search")}
                  // TODO: Preguntar para que es
                  // onBlur={setFieldTouched}
                  conceptId={"705235004"} // Dispositivos
                />
              </FormGroup>
              <FormGroup controlId="formAttachment">
                <DocumentUploadCard
                  attachmentEntityTypeId={attachmentEntityType.Hospital}
                  entityId={deviceId}
                  attachment={formData.attachmentFile}
                  onAttachmentChange={(newAttachment) => {
                    setFormData((prev) => ({
                      ...prev,
                      attachmentFile: newAttachment,
                    }));
                  }}
                  showDownloadButton={false}
                />
              </FormGroup>
              {formData.attachmentFile?.attachmentFileName && (
                <FormGroup controlId="formAttachment">
                  <ControlLabel>{t("devices.editModal.dueDate")}</ControlLabel>
                  <CustomDate
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    inputProps={{ placeholder: "DD/MM/AAAA" }}
                    value={formData.attachmentFile?.dueDate}
                    name="dueDate"
                    onChange={(name, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        attachmentFile: {
                          ...prev.attachmentFile,
                          [name]: value,
                        },
                      }));
                    }}
                    //onBlur={setFieldTouched}
                    viewMode="years"
                    closeOnSelect={true}
                  />
                </FormGroup>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-default"
            onClick={handleAuditTrail}
          >
            {t("devices.editModal.auditTrail")}
          </button>
          <Button variant="light" onClick={onClose}>
            {t("devices.editModal.cancelLabel")}
          </Button>
          {!saving && (
            <Button
              disabled={!formData.device || !formData.name}
              variant="btn btn-primary"
              onClick={handleSubmit}
            >
              {t("devices.editModal.acceptLabel")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {showAuditModal && (
        <AuditModal
          onClose={() => setShowAuditModal(false)}
          title={t("auditTrail.audit")}
        >
          <AuditTrail entity="Device" entityId={deviceId} />
        </AuditModal>
      )}
    </>
  );
};
